import {Dispatch, SetStateAction, useCallback, useMemo, useState} from 'react';
import {mapObject} from 'underscore';

import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import useSimpleEvaluation from 'notebook/simple-tables/useSimpleEvaluation';
import {UpdateProcedureRunParams} from 'procedures/network/ProcedureRunDAO';
import buildFullExperimentPlan from 'procedures/planning/buildFullExperimentPlan';
import {
  ExperimentalInputEvaluatedValues,
  ExperimentalInputs,
  ExperimentalInputsContextProperties,
  ExperimentalInputsPerMethod,
} from 'procedures/planning/ProcedurePlanTypes';
import {
  convertExperimentalInputsToTableMap,
  convertOutputTableMapToEvaluatedValues,
} from 'procedures/planning/useInputTable';
import {CreateStudyModalOptionalParams} from 'studies/sidepanel/NewStudyDropdown';
import {ProcedureInformation, ProcedureRunDataEntryMode} from 'studies/StudyDAO';
import {StudySchema} from 'studies/typed/utils/TypedStudyTypes';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';

import {
  ExperimentFlowchart,
  ExperimentProcedure,
  prepareInitialExperimentalInputs,
  selectFlowchartVersion,
  useExperimentProcedure,
} from '../shared/ExperimentalInputs';
import {
  areProcedureRunInputsEvaluatedValuesValid,
  validateConditions,
} from '../shared/procedureRunStudyValidationUtils';
import {
  MethodExecutionLimitState,
  useMethodExecutionLimitState,
} from '../shared/useMethodExecutionLimitState';
import {serializeExperimentConditionsForNetwork} from './ProcessDevelopmentStudyNetworkTranslators';

export interface ConditionValidationState {
  isDuplicateName: boolean;
  isNameLengthOverLimit: boolean;
  isDescLengthOverLimit: boolean;
}

export interface Condition {
  numReplicates: number;
  name: string;
  description?: string;
  id?: string;
  validationState?: ConditionValidationState;
}

export interface ExperimentDesignState {
  /** User-selected procedure */
  procedureId: string | null;
  /** Setter for {@link procedureId} */
  setProcedureId: Dispatch<SetStateAction<string | null>>;
  /** Data for  {@link procedureId}. `null` iff. procedureId is null or data is still loading. */
  procedure: ExperimentProcedure | null;
  /** Metadata for user-defined conditions. Condition values are stored in experimentalInputs. */
  conditions: Array<Condition> | null;
  /** Setter for {@link conditions} */
  setConditions: (conditions: Array<Condition> | null, prevIndexes: Array<number | null>) => void;
  /** User-specified condition data. `null` iff. {@link procedure} is null or {@link numConditions} is null. */
  experimentalInputs: ExperimentalInputs | null;
  /** Setter for {@link experimentalInputs}. */
  setExperimentalInputs: Dispatch<SetStateAction<ExperimentalInputs>>;
  /** User-selected method. `null` iff. {@link procedure} is null or has no methods. */
  selectedMethodId: string | null;
  /** Setter for {@link selectedMethodId} */
  setSelectedMethodId: Dispatch<SetStateAction<string>>;
  /** Data for ExperimentalInputsContext react context. `null` iff. any of replicate, conditions, procedure are unspecified. */
  context: ExperimentalInputsContextProperties | null;
  /** User-specified procedure run description */
  description: string;
  /** Setter for {@link description} */
  setDescription: Dispatch<SetStateAction<string>>;
  /** User-specified data entry mode. If the study type does not support data entry mode selection, */
  dataEntryMode: ProcedureRunDataEntryMode;
  /** Setter for {@link dataEntryMode}. Unused if the study type does not support data entry mode selection. */
  setDataEntryMode: Dispatch<SetStateAction<ProcedureRunDataEntryMode>>;

  experimentParam: ProcedureInformation | null; // TODO: we need validation information here too...
  methodExecutionLimitState: MethodExecutionLimitState;
}

export interface ProcessDevelopmentStudyState extends ExperimentDesignState {}

/**
 * Hook to validate state and produce a ProcedureInformation that can be used in a CreateStudyParam.
 *
 * @package exported for testing
 */
export function useExperimentParam(
  procedure: ExperimentProcedure | null,
  conditions: Array<Condition> | null,
  description: string,
  experimentalInputs: ExperimentalInputs | null,
  evaluatedValues: ExperimentalInputEvaluatedValues | null,
  dataEntryMode: ProcedureRunDataEntryMode
): ProcedureInformation | null {
  return useMemo(() => {
    if (
      procedure == null ||
      experimentalInputs == null ||
      evaluatedValues == null ||
      conditions == null ||
      !validateConditions(conditions) ||
      !areProcedureRunInputsEvaluatedValuesValid(evaluatedValues)
    ) {
      return null;
    }
    const replicatesPerCondition = conditions.map((condition) => condition.numReplicates);
    const namesPerCondition = conditions.map((condition) => condition.name);
    const descriptionsPerCondition = conditions.map((condition) => condition.description);
    const experimentPlan = buildFullExperimentPlan({
      replicatesPerCondition,
      namesPerCondition,
      descriptionsPerCondition,
      numConditions: replicatesPerCondition.length,
      experimentalInputs,
      evaluatedValues,
    });
    // Backend requires exactly one of [templateProcedureFlowchartVersionId, workflowFlowchartConfigVersionId]
    const {versionId, isPoW} = selectFlowchartVersion(procedure);
    return Object.assign(
      {
        experimentConditions: serializeExperimentConditionsForNetwork(experimentPlan),
        confirmationMethodByParamVersionId: experimentPlan.confirmationMethodByParamVersionId,
        description,
        dataEntryMode,
      },
      isPoW ? {workflowFlowchartConfigVersionId: versionId} : {templateProcedureFlowchartVersionId: versionId}
    );
  }, [procedure, experimentalInputs, evaluatedValues, conditions, description, dataEntryMode]);
}

/**
 * Hook to produce an UpdateProcedureRunParams that can be used to provide a timing estimate.
 */
export function useProcedureRunUpdateParam(
  procedureRunId: string,
  conditions: Array<Condition>,
  experimentalInputs: ExperimentalInputs,
  evaluatedValues: ExperimentalInputEvaluatedValues
): UpdateProcedureRunParams {
  return useMemo(() => {
    // For simplicity, we serialize the full list of conditions and then take only the new conditions at the
    // end rather than modifying every input to the experiment plan to only include the new conditions
    const replicatesPerCondition = conditions.map((condition) => condition.numReplicates);
    const namesPerCondition = conditions.map((condition) => condition.name);
    const descriptionsPerCondition = conditions.map((condition) => condition.description);
    const experimentPlan = buildFullExperimentPlan({
      replicatesPerCondition,
      namesPerCondition,
      descriptionsPerCondition,
      numConditions: replicatesPerCondition.length,
      experimentalInputs,
      evaluatedValues,
    });
    const serializedConditions = serializeExperimentConditionsForNetwork(experimentPlan);
    const serializedNewConditions = serializedConditions.filter((c, index) => conditions[index].id == null);
    return {
      id: procedureRunId,
      newConditions: serializedNewConditions,
      confirmationMethodByParamVersionId: experimentPlan.confirmationMethodByParamVersionId,
    };
  }, [procedureRunId, experimentalInputs, evaluatedValues, conditions]);
}

export function useExperimentalInputsContextProperties(
  conditions: Array<Condition> | null,
  experimentalInputs: ExperimentalInputs | null,
  setExperimentalInputs: ((inputs: ExperimentalInputs) => void) | null,
  flowchart: ExperimentFlowchart | null,
  procedureType: ProcedureType | null,
  isLocked: boolean = false,
  isPlan: boolean = false
): ExperimentalInputsContextProperties | null {
  // Create the table with isLocked=false for evaluation purposes for plans.
  // This ensures validation errors are computed and persist regardless of edit mode.
  // The actual locked state is used for UI locking via context.locked below.
  const fullTableMap = useMemo(
    () =>
      experimentalInputs != null && flowchart != null && procedureType != null && conditions != null
        ? convertExperimentalInputsToTableMap(
            experimentalInputs,
            flowchart,
            procedureType,
            isPlan ? false : isLocked,
            conditions,
            isPlan
          )
        : {},
    [experimentalInputs, flowchart, conditions, procedureType, isPlan, isLocked]
  );
  const simpleTableDoc = useSimpleEvaluation(fullTableMap);
  if (setExperimentalInputs == null) {
    assert(isLocked, 'setExperimentalInputs should only be null for locked editor');
  }

  const evaluatedValues = useMemo(
    () =>
      experimentalInputs != null && flowchart != null && conditions != null
        ? convertOutputTableMapToEvaluatedValues(
            simpleTableDoc.noteRuntimeSnapshot.evaluationSnapshot,
            experimentalInputs
          )
        : null,
    [simpleTableDoc.noteRuntimeSnapshot.evaluationSnapshot, experimentalInputs, flowchart, conditions]
  );

  return useMemo(
    () =>
      experimentalInputs != null && flowchart != null && conditions != null && evaluatedValues != null
        ? {
            numConditions: conditions.length,
            replicatesPerCondition: conditions.map((condition) => condition.numReplicates),
            namesPerCondition: conditions.map((condition) => condition.name),
            descriptionsPerCondition: conditions.map((condition) => condition.description),
            numReplicates: conditions.map((condition) => condition.numReplicates)[0],
            draftInputs: experimentalInputs,
            setExperimentalInputs: (methodId, newInputs, newConfirmationMethods) => {
              assert(setExperimentalInputs != null, 'Unexpected edit on locked design.');
              assert(experimentalInputs != null, 'Experimental inputs must be set before continuing');
              assert(experimentalInputs.hasOwnProperty(methodId), `Unknown methodId: ${methodId}`);
              setExperimentalInputs({
                ...experimentalInputs,
                [methodId]: {
                  ...experimentalInputs[methodId],
                  values: newInputs,
                  parameterConfirmationMethods: newConfirmationMethods,
                },
              });
            },
            locked: isLocked,
            simpleTableDoc,
            evaluatedValues,
          }
        : null,
    [
      conditions,
      experimentalInputs,
      setExperimentalInputs,
      evaluatedValues,
      flowchart,
      isLocked,
      simpleTableDoc,
    ]
  );
}

/**
 * Hook to manage the state for designing a Process Development study via the CreateStudyModal.
 *
 * @returns a {@link ProcessDevelopmentStudyState} object.
 */
export default function useProcessDevelopmentStudyState(
  studySchema: StudySchema,
  initialValues: CreateStudyModalOptionalParams
): ProcessDevelopmentStudyState {
  const experimentDesignState = useExperimentDesignState(
    initialValues,
    ProcedureRunDataEntryMode.ALL_CONDITION_REPLICATES_TOGETHER
  );

  return {...experimentDesignState};
}

/**
 * Hook to manage the state for designing a procedure-based experiment.
 *
 * @returns a {@link ExperimentDesignState} object.
 */
export function useExperimentDesignState(
  initialValues: CreateStudyModalOptionalParams,
  /**
   * The default data entry mode. Note: this is intentionally not exposed as part of {@link initialValues}
   * as it should only be configured on a per-study-type basis.
   */
  initialDataEntryMode: ProcedureRunDataEntryMode
): ExperimentDesignState {
  const [procedureId, setProcedureId] = useState<string | null>(initialValues.initialProcedureId ?? null);
  const [description, setDescription] = useState<string>('');
  const [dataEntryMode, setDataEntryMode] = useState<ProcedureRunDataEntryMode>(initialDataEntryMode);
  const {procedure} = useExperimentProcedure(procedureId ? {procedureId} : null);
  const [selectedMethodId, setSelectedMethodId] = useSelectedMethodId(procedure);
  const {conditions, setConditions, experimentalInputs, setExperimentalInputs} =
    useConditionsAndExperimentalInputs(procedure);
  const context = useExperimentalInputsContextProperties(
    conditions,
    experimentalInputs,
    setExperimentalInputs,
    procedure?.procedureFlowchart ?? null,
    procedure?.procedureType ?? null
  );
  const methodExecutionLimitState = useMethodExecutionLimitState(
    experimentalInputs,
    conditions,
    procedure?.largestTableTypeSizes ?? null,
    dataEntryMode
  );
  const experimentParam = useExperimentParam(
    procedure,
    conditions,
    description,
    experimentalInputs,
    context?.evaluatedValues ?? null,
    dataEntryMode
  );

  return {
    procedure,
    procedureId,
    setProcedureId,
    selectedMethodId,
    setSelectedMethodId,
    conditions,
    setConditions,
    experimentalInputs,
    setExperimentalInputs,
    context,
    methodExecutionLimitState,
    experimentParam,
    description,
    setDescription,
    dataEntryMode,
    setDataEntryMode,
  };
}

/**
 * These two pieces of state are managed together as they are closely related.
 *
 * Behaviors:
 *   - Whenever `procedure` is loading, `experimentalInputs` will be `null`.
 *   - Whenever `procedure` has loaded, `experimentalInputs` will be initialized to the initial values.
 *   - Whenever `numConditions` is updated, `experimentalInputs` will be re-initialized preserving values
 *     when possible.
 */
export function useConditionsAndExperimentalInputs(
  procedure: Pick<ExperimentProcedure, 'id' | 'procedureType' | 'methods'> | null,
  // Initial values are non-null when editing an existing experiment design
  initialProcedureId: string | null = null,
  initialConditions: Array<Condition> | null = null,
  initialExperimentalInputs: ExperimentalInputs | null = null
): {
  experimentalInputs: ExperimentalInputs | null;
  setExperimentalInputs: Dispatch<SetStateAction<ExperimentalInputs | null>>;
  conditions: Array<Condition> | null;
  /**
   * @param conditions - new conditions
   * @param prevIndexes - for the condition, which index in ExperimentalInputs its values can be found.
   *    null if the condition does not have value data yet.
   */
  setConditions: (conditions: Array<Condition> | null, prevIndexes: Array<number | null>) => void;
  /**
   * Atomically reset both conditions and experimental inputs to provided values.
   * Used when canceling edits to restore the original state without stale closure issues.
   */
  resetConditionsAndInputs: (conditions: Array<Condition>, experimentalInputs: ExperimentalInputs) => void;
} {
  const [data, setData] = useState<{
    conditions: Array<Condition> | null;
    experimentalInputs: ExperimentalInputs | null;
    /** procedureId used to generate the experimentalInputs. */
    procedureId: string | null;
  }>({
    conditions: initialConditions,
    experimentalInputs: initialExperimentalInputs,
    procedureId: initialProcedureId,
  });

  // Maintain invariant that experimentalInputs properly reflects procedure and numConditions.
  const {
    conditions: storedConditions,
    experimentalInputs: storedExperimentalInputs,
    procedureId: storedProcedureId,
  } = data;
  let conditions: Array<Condition> | null;
  let experimentalInputs: ExperimentalInputs | null;
  let procedureId: string | null;
  if (procedure == null) {
    conditions = null;
    experimentalInputs = null;
    procedureId = null;
  } else {
    conditions =
      storedConditions != null && procedure.id === storedProcedureId
        ? storedConditions
        : [
            {
              numReplicates: 1,
              name: `${getProcedureRunConditionTextForProcedureType(procedure.procedureType, {
                capital: true,
              })} 1`,
            },
          ];
    experimentalInputs =
      storedExperimentalInputs != null && procedure.id === storedProcedureId
        ? storedExperimentalInputs
        : prepareInitialExperimentalInputs(conditions.length, procedure.methods);
    procedureId = procedure.id;
  }

  if (
    conditions !== storedConditions ||
    experimentalInputs !== storedExperimentalInputs ||
    procedureId !== storedProcedureId
  ) {
    // Store in state so that we can allow user to iterate from the initial state.
    // NOTE: While setting state on render has the potential to cause infinite render loops, we do not expect
    // this to happen as the next experimentalInputs is expected to match the new storedExperimentalInputs.
    setData({...data, conditions, experimentalInputs, procedureId});
  }

  const setConditions = useCallback(
    (conditions: Array<Condition>, prevIndexes: Array<number | null>): void => {
      setData({
        ...data,
        conditions,
        experimentalInputs:
          procedure && experimentalInputs
            ? prepareInitialExperimentalInputs(conditions.length, procedure.methods, {
                prevExperimentalInputValues: mapObject(
                  experimentalInputs,
                  (inputsPerMethod: ExperimentalInputsPerMethod) => ({
                    // When columns are added/removed, make sure values shift accordingly.
                    values: prevIndexes.map((index) =>
                      index != null ? inputsPerMethod.values[index] : inputsPerMethod.defaultValues
                    ),
                  })
                ),
                // Preserve confirmation methods since they apply across all conditions
                prevConfirmationMethods: experimentalInputs,
              })
            : null,
      });
    },
    [data, experimentalInputs, procedure]
  );

  const setExperimentalInputs = useCallback(
    (newExperimentalInputs: ExperimentalInputs) => {
      assert(procedure != null, 'Procedure must be loaded before setting experimental inputs.');
      setData({...data, experimentalInputs: newExperimentalInputs, procedureId: procedure.id});
    },
    [data, procedure]
  );

  const resetConditionsAndInputs = useCallback(
    (newConditions: Array<Condition>, newExperimentalInputs: ExperimentalInputs) => {
      assert(procedure != null, 'Procedure must be loaded before resetting conditions and inputs.');
      setData({
        conditions: newConditions,
        experimentalInputs: newExperimentalInputs,
        procedureId: procedure.id,
      });
    },
    [procedure]
  );

  return {conditions, setConditions, experimentalInputs, setExperimentalInputs, resetConditionsAndInputs};
}

/**
 * Hook to manage the selected methodId of a loaded procedure.
 *
 * @returns `[selectedMethodId, setSelectedMethodId]`
 *  - `selectedMethodId` as defined in {@link ProcessDevelopmentStudyState.selectedMethodId}.
 *  - `setSelectedMethodId` as defined in {@link ProcessDevelopmentStudyState.setSelectedMethodId}.
 */
function useSelectedMethodId(
  procedure: ExperimentProcedure | null
): [string | null, Dispatch<SetStateAction<string | null>>] {
  const [storedMethodId, setSelectedMethodId] = useState<string | null>(null);
  let selectedMethodId: string | null;
  if (procedure == null) {
    // No procedure is loaded, so no method should be selected yet.
    selectedMethodId = null;
  } else if (storedMethodId == null || !procedure.methods.some((m) => m.id === storedMethodId)) {
    // Selected method is incomptible with the loaded procedure, reset to the first.
    selectedMethodId = procedure.methods[0]?.id ?? null;
  } else {
    // Selected method is compatible with the loaded procedure.
    selectedMethodId = storedMethodId;
  }
  if (selectedMethodId !== storedMethodId) {
    // Store in state so that we can allow user to iterate from the initial state.
    // NOTE: While setting state on render has the potential to cause infinite render loops, we do not expect
    // this to happen as the next selectedMethodId is expected to match the new storedMethodId.
    setSelectedMethodId(selectedMethodId);
  }
  return [selectedMethodId, setSelectedMethodId];
}
